import React, { useState, useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { ChevronDown } from "lucide-react";
import { FormField, FormItem, FormMessage } from "./form";
import { AssetSelectionModal } from "./asset-selection-modal";
import { Asset } from "../../types/asset";
import { useAssets } from "../../hooks/useAssets";
import { UI_CONFIG } from "../../constants/autostack";

interface AssetSelectorProps {
  name: string;
  assets: Asset[];
  selectedAsset?: Asset;
  className?: string;
  placeholder?: string;
  label?: string;
}

export const AssetSelector: React.FC<AssetSelectorProps> = React.memo(
  ({
    name,
    assets,
    selectedAsset,
    className = "",
    placeholder = "Select an asset",
    label = "Asset",
  }) => {
    const form = useFormContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { getAssetByValue } = useAssets();

    const currentValue = form.watch(name);
    const currentAsset = selectedAsset || getAssetByValue(currentValue);

    const handleAssetSelect = useCallback(
      (asset: Asset) => {
        form.setValue(name, asset.asset);
        setIsModalOpen(false);
      },
      [form, name]
    );

    const handleOpenModal = useCallback(() => {
      setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
      setIsModalOpen(false);
    }, []);

    return (
      <>
        <FormField
          control={form.control}
          name={name}
          render={({ field }) => (
            <FormItem className={className}>
              <div className="flex flex-col gap-1 w-full md:w-auto">
                <button
                  type="button"
                  onClick={handleOpenModal}
                  className={`
                  flex items-center justify-between w-full min-w-full md:min-w-[195px] ${UI_CONFIG.FORM_HEIGHT} px-4 
                  border border-gray-200 rounded-md bg-white hover:bg-gray-50 
                  focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 
                  transition-colors
                `}
                  aria-label={`Select ${label}`}
                  aria-expanded={isModalOpen}
                  aria-haspopup="dialog"
                >
                  {currentAsset ? (
                    <div className="flex items-center space-x-3">
                      <img
                        src={currentAsset.image}
                        alt={currentAsset.assetName}
                        className="w-6 h-6 rounded-full"
                      />
                      <span>{currentAsset.assetName}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">{placeholder}</span>
                  )}
                  <ChevronDown
                    className="h-4 w-4 text-muted-foreground"
                    aria-hidden="true"
                  />
                </button>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <AssetSelectionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          assets={assets}
          onAssetSelect={handleAssetSelect}
          selectedAsset={currentAsset}
          title={`Select ${label}`}
        />
      </>
    );
  }
);

AssetSelector.displayName = "AssetSelector";
